import React, { useEffect, useRef, useState } from "react";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import ContainerWide from "../components/ContainerWide";
import { VectorBackground } from "../components/VectorBackground";

const isHtmlFile = (src) => /\.html?(?:[?#].*)?$/i.test(src || "");

// <html> box includes body margins and child margins collapsing through body
// (scrollHeight would never shrink below the current iframe height)
const getDocumentHeight = (doc) =>
    Math.ceil(doc?.documentElement?.getBoundingClientRect().height || 0);

// Same-origin HTML animations can grow with their content (e.g. the stacked
// mobile layout), so a fixed aspect ratio would cut them off
const useIframeContentHeight = (enabled) => {
    const iframeRef = useRef(null);
    const observerRef = useRef(null);
    const [height, setHeight] = useState(null);

    const handleLoad = () => {
        if (!enabled) return;

        const doc = iframeRef.current?.contentDocument;
        if (!doc?.body) return;

        const update = () => {
            const nextHeight = getDocumentHeight(doc);
            if (nextHeight > 0) setHeight(nextHeight);
        };

        update();
        observerRef.current?.disconnect();
        if (typeof ResizeObserver !== "undefined") {
            observerRef.current = new ResizeObserver(update);
            observerRef.current.observe(doc.body);
        }
    };

    useEffect(() => {
        // The iframe may finish loading before hydration attaches onLoad
        const doc = iframeRef.current?.contentDocument;
        if (doc?.readyState === "complete" && doc.URL !== "about:blank") {
            handleLoad();
        }

        return () => observerRef.current?.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { iframeRef, handleLoad, height };
};

const LottieAsset = ({ src, className, aspectRatio, title, autoHeight }) => {
    const isHtml = isHtmlFile(src);
    const { iframeRef, handleLoad, height } = useIframeContentHeight(
        isHtml && autoHeight
    );

    if (isHtml) {
        return (
            <iframe
                ref={iframeRef}
                onLoad={handleLoad}
                src={src}
                title={title}
                className={`${className} lottie__iframe`}
                style={height ? { height } : { aspectRatio }}
                loading="lazy"
                scrolling="no"
                sandbox="allow-scripts allow-same-origin"
            />
        );
    }

    return (
        <DotLottieReact
            src={src}
            loop
            autoplay
            className={className}
            style={{ aspectRatio }}
            layout={{ fit: "cover" }}
        />
    );
};

const Lottie = ({
    category,
    header,
    description,
    lottieSrc,
    lottieSrcMobile,
    bottomText,
    size,
    slug,
    variant,
}) => {
    const desktopSrc = lottieSrc?.localFile?.publicURL;
    const mobileSrc = lottieSrcMobile?.localFile?.publicURL;
    const isLight = variant === "light";

    return (
        <ContainerWide
            className={`lottie__container${
                isLight ? " lottie__container--light" : ""
            }`}
            id={slug || undefined}
        >
            {!isLight && <VectorBackground gradient strokeOpacity={0.07} />}
            <div className="lottie__visual">
                {(category || header || description) && (
                    <div className="lottie__intro">
                        {category && (
                            <p
                                className="lottie__label"
                                dangerouslySetInnerHTML={{ __html: category }}
                            />
                        )}
                        {header && (
                            <h2
                                className="lottie__header"
                                dangerouslySetInnerHTML={{ __html: header }}
                            />
                        )}
                        {description && (
                            <div
                                className="lottie__description"
                                dangerouslySetInnerHTML={{
                                    __html: description,
                                }}
                            />
                        )}
                    </div>
                )}
                {desktopSrc && (
                    <LottieAsset
                        src={desktopSrc}
                        className="lottie__animation lottie__animation--desktop"
                        aspectRatio="1280/520"
                        autoHeight
                        title={
                            header
                                ? header.replace(/<[^>]*>?/gm, "")
                                : "Animation"
                        }
                    />
                )}
                {mobileSrc && (
                    <LottieAsset
                        src={mobileSrc}
                        className="lottie__animation lottie__animation--mobile"
                        aspectRatio="390/600"
                        autoHeight
                        title={
                            header
                                ? header.replace(/<[^>]*>?/gm, "")
                                : "Animation"
                        }
                    />
                )}
            </div>
            {bottomText && (
                <p
                    className="lottie__bottom-text"
                    dangerouslySetInnerHTML={{ __html: bottomText }}
                />
            )}
        </ContainerWide>
    );
};

export default Lottie;
